/**
 * 商品選択モジュール
 * マッチングエンジンが返した上位3商品をカード形式で表示する。
 * 既存のCSSクラス・世界観をそのまま使用する。
 */

import { state } from './state';
import { addMsg, setInputArea } from './chat';
import { getUserNameForDisplay } from './profile';
import { openUrl } from './liff';

/** 推薦商品の型（APIレスポンスに合わせた形式） */
interface Recommendation {
  rank: number;
  score: number;
  score_breakdown?: { element: number; aura: number; theme: number; worry: number };
  woo_product_id: number;
  sku: string;
  product_name: string;
  price: string | number;
  product_url: string;
  stones: string[];
  stone_colors: string[];
  recommendation_reason: string;
  theme_tags?: string[];
  oracle_card?: { name: string; is_upright: boolean };
  diagnosis_message?: string;
}

/** 英語カラータグ → 日本語表記 */
const COLOR_JA: Record<string, string> = {
  blue:    '青',
  gold:    'ゴールド',
  orange:  'オレンジ',
  red:     '赤',
  green:   '緑',
  purple:  '紫',
  pink:    'ピンク',
  white:   '白',
  black:   '黒',
  gray:    'グレー',
  grey:    'グレー',
  yellow:  'イエロー',
  brown:   'ブラウン',
  clear:   'クリア',
  silver:  'シルバー',
  rainbow: '虹色',
};

/** 英語カラータグ → パステルカラー（グラデーション表現用） */
const PASTEL_COLOR: Record<string, string> = {
  blue:    '#cfe4f5',
  gold:    '#f3e6c4',
  orange:  '#f9dcc4',
  red:     '#f6d3d6',
  green:   '#d7ecdc',
  purple:  '#e2d9f0',
  pink:    '#f8dbe8',
  white:   '#f6f4ee',
  black:   '#d9d9de',
  gray:    '#e2e4e6',
  grey:    '#e2e4e6',
  yellow:  '#f7edc2',
  brown:   '#e9dcc9',
  clear:   '#eef1f1',
  silver:  '#e6e8ea',
  rainbow: '#e9dcf3',
};
const PASTEL_FALLBACK = '#e7ece9';

/** 石の色構成から、パステルカラーのグラデーションCSSを組み立てる */
function buildPastelGradient(stoneColors: string[]): string {
  const pastels = (stoneColors || [])
    .map(c => PASTEL_COLOR[c] || null)
    .filter((c): c is string => Boolean(c));
  const colors = pastels.length > 0 ? pastels : [PASTEL_FALLBACK];
  if (colors.length === 1) {
    colors.push(colors[0]);
  }
  return `linear-gradient(135deg, ${colors.join(', ')})`;
}

/** 棒グラフの色（濃い灰色1色に統一） */
const BAR_COLOR = '#4a4f52';

/** 単一スコアバー行（ラベル・バー・パーセントを1行で表示） */
function buildSingleBar(label: string, pct: number): string {
  return `
    <div style="margin-bottom:6px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
        <span style="font-size:12px;color:rgba(31,37,39,0.68);">${label}</span>
        <span style="font-size:13px;font-weight:700;color:#172123;">${pct}%</span>
      </div>
      <div style="background:rgba(40,87,96,0.12);border-radius:999px;height:6px;overflow:hidden;">
        <div style="width:${pct}%;height:100%;background:${BAR_COLOR};border-radius:6px;transition:width .6s ease;"></div>
      </div>
    </div>
  `;
}

/** 一致率バーと内訳HTML（アコーディオン対応） */
function buildScoreBar(score: number, breakdown?: { element: number; aura: number; theme: number; worry: number }, cardId?: string): string {
  const pct = Math.min(100, Math.round(score));
  const breakdownId = cardId ? `breakdown-${cardId}` : '';
  const breakdownHtml = breakdown ? `
    <div id="${breakdownId}" style="display:none;margin-top:10px;border-top:1px solid rgba(36,45,54,0.12);padding-top:8px;">
      ${buildSingleBar('星座相性', Math.min(100, Math.round(breakdown.element)))}
      ${buildSingleBar('オーラ',   Math.min(100, Math.round(breakdown.aura)))}
      ${buildSingleBar('テーマ',   Math.min(100, Math.round(breakdown.theme)))}
      ${buildSingleBar('悩み',     Math.min(100, Math.round(breakdown.worry)))}
    </div>
  ` : '';
  return `
    <div style="margin:6px 0 2px;">
      <div style="cursor:pointer;user-select:none;" onclick="toggleScoreBreakdown('${breakdownId}', this)">
        ${buildSingleBar('星読み一致率', pct)}
      </div>
      ${breakdownHtml}
    </div>
  `;
}

/** 商品1枚分のカードHTML（既存の result-section クラスを使用） */
function buildProductCard(rec: Recommendation, idx: number, isSelected: boolean): string {
  const rank = rec.rank ?? idx + 1;
  const rankLabel = rank === 1 ? '✨ 第1位' : rank === 2 ? '🌙 第2位' : '⭐ 第3位';
  const priceText = rec.price ? `¥${Number(rec.price).toLocaleString()}` : '';
  const stonesText = (rec.stones || [])
    .map((name, i) => {
      const colorKey = (rec.stone_colors || [])[i] || '';
      const colorJa = COLOR_JA[colorKey] || '';
      return colorJa ? `${name}（${colorJa}）` : name;
    })
    .join(' × ') || '—';
  const reason = rec.recommendation_reason || 'あなたの星読みに共鳴する構成です';
  const themeText = (rec.theme_tags || []).join(' / ');

  // 写真の代わりに、使用石の色をパステルカラーのグラデーションで表現する
  const colorSwatchHtml = `
    <div style="width:100%;height:56px;border-radius:10px;margin:8px 0;background:${buildPastelGradient(rec.stone_colors)};"></div>
  `;

  const activeStyle = isSelected
    ? 'border:2px solid #d1ac67;box-shadow:0 0 0 1px rgba(209,172,103,0.22),0 14px 30px rgba(0,0,0,0.16);'
    : 'border:1px solid rgba(36,45,54,0.12);';

  return `
    <div
      class="product-card"
      style="background:linear-gradient(180deg,rgba(255,255,255,0.96),rgba(237,244,243,0.92));border-radius:8px;padding:14px;margin-bottom:10px;cursor:pointer;transition:all .15s ease;${activeStyle}"
      onclick="selectProductCandidate(${idx}, this)"
    >
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
        <span style="font-size:15px;font-weight:800;color:#172123;">${rankLabel}</span>
        ${priceText ? `<span style="font-size:14px;font-weight:700;color:#285760;">${priceText}</span>` : ''}
      </div>
      ${colorSwatchHtml}
      <p style="font-size:15px;font-weight:800;color:#172123;margin:4px 0 6px;line-height:1.5;">${rec.product_name || rec.sku || `候補${rank}`}</p>
      <p style="font-size:13px;color:rgba(31,37,39,0.68);margin:0 0 6px;">使用石：${stonesText}</p>
      ${themeText ? `<p style="font-size:13px;color:rgba(31,37,39,0.68);margin:0 0 6px;">テーマ：${themeText}</p>` : ''}
      ${buildScoreBar(rec.score || 0, rec.score_breakdown, `card-${idx}`)}
      <p style="font-size:13px;color:rgba(31,37,39,0.76);margin-top:8px;line-height:1.6;">${reason}</p>
    </div>
  `;
}

/** 推薦ブレスレット候補を表示 */
export function showProductCandidates(): void {
  const recs = state.productCandidates as unknown as Recommendation[];

  if (!recs || recs.length === 0) {
    addMsg('関連するブレスレット候補が見つかりませんでした。', false);
    return;
  }

  if (state.selectedProductIndex === null && recs.length > 0) {
    state.selectedProductIndex = 0;
  }

  const nameForDisplay = getUserNameForDisplay();

  let cardsHtml = '';
  recs.forEach((rec, idx) => {
    cardsHtml += buildProductCard(rec, idx, idx === state.selectedProductIndex);
  });

  const html = `
    <div class="result-section">
      <h3 style="margin-bottom:6px;font-size:15px;">${nameForDisplay}へのおすすめブレスレット</h3>
      <p style="font-size:13px;color:rgba(31,37,39,0.68);margin-bottom:12px;line-height:1.6;">
        星読みの結果から、最もあなたに共鳴する3つをお選びしました。<br>
        気になる1本を選んで、詳しいページをご覧ください。
      </p>
      ${cardsHtml}
    </div>
    <button class="btn" onclick="goToSelectedProduct()">この石を詳しく見る</button>
    <button class="btn btn-secondary" onclick="goLineRegister()">LINEでオラクルカードを受け取る</button>
    <button class="btn btn-secondary" onclick="restartFromBeginning()">もう一度診断する</button>
  `;

  setInputArea(html);
}

/** 商品カードを選択 */
export function selectProductCandidate(index: number, el: HTMLElement): void {
  state.selectedProductIndex = index;

  // カード全体の親を検索してスタイルをリセット
  const container = el.closest('.result-section') || el.parentElement;
  container?.querySelectorAll('.product-card').forEach(card => {
    (card as HTMLElement).style.border = '1px solid rgba(36,45,54,0.12)';
    (card as HTMLElement).style.boxShadow = 'none';
  });
  el.style.border = '2px solid #d1ac67';
  el.style.boxShadow = '0 0 0 1px rgba(209,172,103,0.22),0 14px 30px rgba(0,0,0,0.16)';
}

/** 選択したブレスレットのページへ遷移 */
export function goToSelectedProduct(): void {
  const recs = state.productCandidates as unknown as Recommendation[];

  if (!recs || recs.length === 0 || state.selectedProductIndex === null) {
    addMsg('先に、見てみたいブレスレットを1本選んでください。', false);
    return;
  }

  const rec = recs[state.selectedProductIndex];
  if (!rec) {
    addMsg('先に、見てみたいブレスレットを1本選んでください。', false);
    return;
  }

  const diagnosisId = window.diagnosisId;

  // ブレスレット選択をシートに記録（fire-and-forget）
  fetch('/api/select-product', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id:       state.userId || '',
      diagnosis_id:  diagnosisId || '',
      rank:          rec.rank,
      woo_product_id: rec.woo_product_id,
      sku:           rec.sku,
      product_name:  rec.product_name,
      score:         rec.score,
    }),
  }).catch(() => { /* サイレント失敗 */ });

  if (rec.product_url) {
    const url = diagnosisId
      ? `${rec.product_url}${rec.product_url.includes('?') ? '&' : '?'}d=${diagnosisId}`
      : rec.product_url;
    openUrl(url);
  } else if (rec.woo_product_id) {
    const base = `https://spicastar.info/atlas/?p=${rec.woo_product_id}`;
    const url = diagnosisId ? `${base}&d=${diagnosisId}` : base;
    openUrl(url);
  } else {
    addMsg('商品ページの情報が見つかりませんでした。', false);
  }
}

/** LINE登録ページへ遷移 */
export function goLineRegister(): void {
  const diagnosisId = window.diagnosisId;
  const recs = state.productCandidates as unknown as Recommendation[];
  const selected = recs?.[state.selectedProductIndex ?? 0] ?? null;

  const lines = [
    '診断結果をLINEでも受け取りたいです。',
    diagnosisId ? `診断ID: ${diagnosisId}` : '',
    selected?.woo_product_id ? `商品ID: ${selected.woo_product_id}` : '',
  ].filter(Boolean);

  const text = encodeURIComponent(lines.join('\n'));
  window.location.href = `https://line.me/R/oaMessage/@586spjck/?text=${text}`;
}

/** スコア詳細のアコーディオンを切り替える */
export function toggleScoreBreakdown(elementId: string, clickedElement: HTMLElement): void {
  if (!elementId) return;

  const breakdownEl = document.getElementById(elementId);
  if (!breakdownEl) return;

  const isVisible = breakdownEl.style.display !== 'none';

  // 他の詳細を閉じる
  document.querySelectorAll('[id^="breakdown-"]').forEach(el => {
    (el as HTMLElement).style.display = 'none';
  });

  // クリックされた要素の詳細をトグル
  if (!isVisible) {
    breakdownEl.style.display = 'block';
    clickedElement.style.fontWeight = '600';
    clickedElement.style.color = '#172123';
  } else {
    breakdownEl.style.display = 'none';
    clickedElement.style.fontWeight = 'normal';
  }
}
